import { AiBadge } from "@/components/AiBadge";
import { BrandHome } from "@/components/BrandHome";
import { WaAlerts } from "@/components/whatsapp/WaAlerts";
import React, { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation, Link } from "react-router-dom";
import { Crown, ShoppingCart,
  LayoutDashboard, FolderKanban, Sparkles, Wand2, CalendarClock,
  Library, BarChart3, LogOut, Menu, X, CreditCard, Target, LineChart, Layers, FileText, FileBarChart, Home, LayoutGrid, Share2, MessageCircle, Mail,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useProjects } from "@/context/ProjectContext";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import ScrollToTop from "@/components/ScrollToTop";
import { ShareButton } from "@/components/ShareButton";
import { InstallAppButton } from "@/components/InstallAppButton";
import { useLang } from "@/context/LangContext";
import { LangToggle } from "@/components/LangToggle";
import { ThemeToggle } from "@/components/ThemeToggle";
import { NotificationBell } from "@/components/NotificationBell";
import { StudioSwitcher } from "@/components/StudioSwitcher";

const navGroups = [
  { title: null, items: [
    { to: "/app", end: true, label: "لوحة العمل", en: "Workspace", icon: LayoutGrid, id: "workspace" },
  ]},
  { title: "التوليد الفردي", en: "Individual Generation", items: [
    { to: "/app/studio?individual=1", match: (loc) => loc.pathname === "/app/studio" && loc.search.includes("individual=1"), label: "التوليد الفردي", en: "Individual Generation", icon: Wand2, id: "individual" },
  ]},
  { title: "المشاريع والاستراتيجيات", en: "Projects & Strategies", items: [
    { to: "/app/projects", label: "المشاريع", en: "Projects", icon: FolderKanban, id: "projects" },
    { to: "/app/strategies", label: "الاستراتيجيات", en: "Strategies", icon: Layers, id: "strategies" },
  ]},
  { title: "المحتوى والتقارير", en: "Content & Reports", items: [
    { to: "/app/library", label: "مكتبة المحتوى", en: "Content Library", icon: Library, id: "library" },
    { to: "/app/reports", label: "تقارير المشاريع", en: "Project Reports", icon: FileBarChart, id: "reports" },
    { to: "/app/analytics", label: "التقارير والتحليلات", en: "Analytics", icon: BarChart3, id: "analytics" },
    { to: "/app/social", label: "قنوات التواصل", en: "Social Channels", icon: Share2, id: "social" },
    { to: "/app/whatsapp-bot", label: "واتساب بوت", en: "WhatsApp Bot", icon: MessageCircle, id: "whatsapp-bot", tint: "text-emerald-500" },
    { to: "/app/email-bot", label: "إيميل بوت", en: "Email Bot", icon: Mail, id: "email-bot", tint: "text-sky-500" },
  ]},
  { title: "الحساب", en: "Account", items: [
    { to: "/app/packages", label: "الباقات والأرصدة", en: "Plans & Credits", icon: ShoppingCart, id: "packages" },
  ]},
];

const adminGroup = { title: "الإدارة", en: "Administration", items: [
  { to: "/app/admin-overview", label: "لوحة الإدارة", en: "Admin Panel", icon: LineChart, id: "admin-overview" },
  { to: "/app/blog-admin", label: "إدارة المدونة", en: "Blog Admin", icon: FileText, id: "blog-admin" },
]};

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const { t, dir } = useLang();
  const { activeProjects, projects: allProjects, activeId, selectProject } = useProjects();
  const projects = activeProjects.some((p) => p.id === activeId) ? activeProjects : [...activeProjects, ...allProjects.filter((p) => p.id === activeId)];  // المشروع النشط المؤرشف يبقى ظاهرًا باسمه
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const SidebarInner = (
    <div className="flex flex-col h-full">
      <div className="px-4 py-6" data-testid="sidebar-logo"><BrandHome /></div>
      <nav className="flex-1 px-3 space-y-1 mt-2 overflow-y-auto">
        {[...navGroups, ...(user?.role === "admin" ? [adminGroup] : [])].map((group, gi) => (
          <div key={gi} className={group.title ? "pt-3" : ""}>
            {group.title && (
              <div className="px-4 pb-1.5 text-[11px] font-bold text-muted-foreground/70 tracking-wide">{t(group.title, group.en)}</div>
            )}
            {group.items.map((n) => (
              <NavLink
                key={n.id}
                to={n.to}
                end={n.end}
                data-testid={`nav-${n.id}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                    (n.match ? n.match(location) : isActive)
                      ? "bg-primary/15 text-primary border border-primary/30"
                      : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                  }`
                }
              >
                <n.icon className={`w-5 h-5 ${n.tint || ""}`} />
                {t(n.label, n.en)}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>
      <div className="p-4 border-t border-border">
        <Link to="/" onClick={() => setOpen(false)} className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-semibold text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors" data-testid="sidebar-home-link">
          <Home className="w-5 h-5" /> {t("الصفحة الرئيسية", "Home page")}
        </Link>
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="w-9 h-9 rounded-full bg-accent/20 text-accent flex items-center justify-center font-bold">
            {user?.name?.[0] || "؟"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate">{user?.name}</div>
            <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
          </div>
        </div>
        <Button
          variant="ghost"
          onClick={handleLogout}
          data-testid="logout-btn"
          className="w-full justify-start gap-3 text-muted-foreground hover:text-destructive mt-1"
        >
          <LogOut className="w-5 h-5" /> {t("تسجيل الخروج", "Log out")}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background grid-bg">
      <WaAlerts />
      {/* Desktop sidebar (right side for RTL) */}
      <aside className={`hidden lg:flex fixed top-0 h-full w-72 glass border-border z-40 ${dir === "ltr" ? "left-0 border-r" : "right-0 border-l"}`}>
        {SidebarInner}
      </aside>

      {/* Mobile sidebar */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <aside className={`relative h-full w-72 glass border-border ${dir === "ltr" ? "ml-auto border-r" : "mr-auto border-l"}`}>
            <button
              className="absolute top-4 left-4 text-muted-foreground"
              onClick={() => setOpen(false)}
              data-testid="close-sidebar"
            >
              <X className="w-6 h-6" />
            </button>
            {SidebarInner}
          </aside>
        </div>
      )}

      <div className={dir === "ltr" ? "lg:ml-72" : "lg:mr-72"}>
        <header className="glass border-b border-border sticky top-0 z-40 px-4 sm:px-8 py-3 sm:py-4 flex flex-wrap items-center gap-3 sm:gap-4" style={{ position: "sticky", top: 0, WebkitTransform: "translateZ(0)" }} data-testid="app-header">
          <button
            className="lg:hidden text-foreground"
            onClick={() => setOpen(true)}
            data-testid="open-sidebar"
          >
            <Menu className="w-6 h-6" />
          </button>
          <Link to="/" className="inline-flex items-center gap-1.5 h-9 rounded-full border border-primary/40 bg-secondary/80 text-primary hover:border-primary px-3 text-sm font-semibold transition-colors" data-testid="header-home-btn" title="الصفحة الرئيسية">
            <Home className="w-4 h-4" /> <span className="hidden sm:inline">{t("الرئيسية", "Home")}</span>
          </Link>
          <ShareButton />
          <InstallAppButton />
          <LangToggle />
          <ThemeToggle />
          <NotificationBell className="h-9 w-9 rounded-full" />
          <Link to="/app/packages#credits" className="inline-flex items-center gap-1.5 h-9 rounded-full bg-accent text-accent-foreground hover:bg-accent/90 px-3 text-sm font-bold transition-colors" data-testid="header-subscribe-btn" title={t("الباقات والأرصدة — اشحن أو اشترك", "Plans & credits")}>
            <ShoppingCart className="w-4 h-4" /> <span className="hidden sm:inline">{t("الباقات والأرصدة", "Plans & Credits")}</span>
          </Link>
          <AiBadge compact testId="header-ai-badge" />
          <Link to="/app" data-testid="workspace-btn"
            className={`order-last lg:order-none w-full lg:w-auto inline-flex items-center justify-center gap-2 h-10 lg:h-9 rounded-full px-5 text-sm font-bold transition-colors ${location.pathname === "/app" ? "bg-primary text-white" : "bg-primary/15 text-primary border border-primary/40 hover:bg-primary hover:text-white"}`}>
            <LayoutGrid className="w-4 h-4" /> {t("لوحة العمل", "Workspace")}
          </Link>
          <div className="flex-1" />
          <div className="flex items-center gap-2">
            {location.search.includes("individual=1") ? (
              <StudioSwitcher t={t} />
            ) : <>
            <span className="text-sm text-muted-foreground hidden sm:inline">{t("المشروع النشط:", "Active project:")}</span>
            {projects.length > 0 ? (<>
              {/* الجوال: قائمة أصلية (لا قفل تمرير/pointer-events من Radix على iOS) */}
              <select value={activeId || ""} onChange={(e) => selectProject(e.target.value)} className="md:hidden h-9 w-[180px] rounded-md bg-secondary border border-border px-2 text-sm font-semibold" data-testid="active-project-select-mobile">
                {!activeId && <option value="">{t("اختر مشروعاً", "Choose a project")}</option>}
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <Select value={activeId || ""} onValueChange={selectProject}>
                <SelectTrigger className="hidden md:flex w-[180px] bg-secondary border-border" data-testid="active-project-select">
                  <SelectValue placeholder={t("اختر مشروعاً", "Choose a project")} />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  {projects.map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`project-option-${p.id}`}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </>) : (
              <Button size="sm" variant="outline" onClick={() => navigate("/app/projects")} data-testid="header-add-project">
                {t("أضف مشروعاً", "Add a project")}
              </Button>
            )}
            </>}
          </div>
        </header>
        <main className="p-4 sm:p-8 max-w-[1400px] mx-auto">
          <Outlet />
        </main>
      </div>
      <ScrollToTop />
    </div>
  );
}
